import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import {
  Stethoscope, MessageSquare, Users, Search,
  LogOut, Menu, X, ArrowLeft,
  Shield, Home, History, AlertTriangle, Award, ScanLine, ClipboardList
} from "lucide-react";
import { Avatar, AvatarFallback } from "./ui/avatar";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0d053155-ce00-4580-8f42-e450395a24b2/artifacts/1forsbvk_logo%20cmes.png";

const sidebarItems = [
  { path: "/pharmatech", icon: Home, label: "Tableau de bord" },
  // { path: "/pharmatech/copilot", icon: Stethoscope, label: "Copilot Pharmacien" }, // Fusionné avec Scanner Ordonnances (mai 2026)
  { path: "/pharmatech/chat", icon: MessageSquare, label: "PharmaGPT" },
  { path: "/pharmatech/profil-patient", icon: Users, label: "Profil Patient Virtuel" },
  { path: "/pharmatech/recherche", icon: Search, label: "Google du Medicament" },
  { path: "/pharmatech/historique", icon: History, label: "Historique Analyses" },
  { path: "/pharmatech/pharmacovigilance", icon: AlertTriangle, label: "Pharmacovigilance" },
  { path: "/pharmatech/ordonnances", icon: ScanLine, label: "Scanner Ordonnances" },
  { path: "/pharmatech/fiches-conseil", icon: ClipboardList, label: "Fiches Conseil" },
  { path: "/pharmatech/score", icon: Award, label: "Score Qualité" },
];

export default function PharmatechLayout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const initials = (user?.full_name || user?.email || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)" }} data-testid="pharmatech-layout">
      {sidebarOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-60 flex flex-col transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{ backgroundColor: "#0f172a", borderRight: "1px solid rgba(255,255,255,0.05)" }}>
        {/* Logo */}
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/5">
          <Link to="/hub" className="flex items-center gap-2.5 group">
            <ArrowLeft className="w-4 h-4 text-slate-500 group-hover:text-blue-400 transition-colors" />
            <img src={LOGO_URL} alt="CMES" className="h-8 w-auto" />
            <div>
              <span className="font-bold text-blue-400 text-sm tracking-tight">CMES</span>
              <span className="block text-[9px] text-slate-500 tracking-widest font-semibold">PHARMATECH</span>
            </div>
          </Link>
          <button className="lg:hidden ml-auto text-slate-400" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-auto">
          {sidebarItems.map(item => {
            const active = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path.split("/").pop()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  active
                    ? "bg-blue-500/15 text-blue-400 font-semibold border-l-2 border-blue-500"
                    : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                }`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="px-3 py-4 border-t border-white/5">
          <div className="flex items-center gap-2 px-3">
            <Avatar className="w-7 h-7">
              <AvatarFallback className="text-[10px] font-bold bg-blue-500/20 text-blue-400">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white font-medium truncate">{user?.full_name || user?.email}</p>
            </div>
            <button onClick={() => { logout(); navigate("/login"); }} className="text-slate-500 hover:text-red-400 transition-colors" data-testid="pharmatech-logout">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-0">
        {/* Top bar */}
        <header className="flex items-center gap-4 px-4 lg:px-8 py-4 border-b border-slate-200/50 bg-white/80 backdrop-blur-sm">
          <button className="lg:hidden text-slate-400" onClick={() => setSidebarOpen(true)} data-testid="pharmatech-menu-toggle">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-800">{title}</h1>
            {subtitle && <p className="text-xs mt-0.5 text-slate-500">{subtitle}</p>}
          </div>
          <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
            <Shield className="w-3 h-3" />
            Outil d'aide - Ne se substitue pas au pharmacien
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
